import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, tap, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { CriteriaRequestRemuneration, RemunerationStatsResponse, RequestRemuneration } from '../../models/remunerations.models';
import { CompaniesResponse, Company } from '../../models/companies.models';
import { CentersResponse } from '../../models/centers.models';
import { BillingStatusesResponse } from '../../models/billing-statuses.models';
import { CollectionStatusesResponse } from '../../models/collection-statuses.models';
import { LiquidationStatusesResponse } from '../../models/liquidation-statuses.models';
import { FinanciersResponse } from '../../models/financiers.models';
import { InsurancesResponse } from '../../models/insurances.models';
import { AreasResponse } from '../../models/areas.models';
import { SpecialtiesResponse } from '../../models/specialties.models';
import { ORDERABLE_COLUMNS } from '../../models/honorary';
import { GroupsResponse } from '../../models/groups.models';
import { SocietiesResponse } from '../../models/societies.models';
import { Doctor, DoctorsResponse } from '../../models/doctors.models';
import { User, UserDetail, UserDetailResponse } from '../../models/user.models';
import { FormControl } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class FeeService {
  private managerSocietiesIds: string[] = [];

  constructor( private http: HttpClient ) { }

  getCompanies(externalsIds?: string[]){
    const body = externalsIds ? {externalUserIds: externalsIds} : {}
    return this.http.post<CompaniesResponse>(`${environment.URL_API}companies`, body).pipe(
      map( (response: CompaniesResponse ) => {
        return response.companies;
      })
    );
  }

  getSocieties(){
    this.managerSocietiesIds = [];
    return this.http.get<SocietiesResponse>(`${environment.URL_API}societies/user/managerSocieties`).pipe(
      map( (response: SocietiesResponse ) => {
        response.societies.forEach(society => {
          this.managerSocietiesIds.push(society.societyId);
        });
        return response.societies;
      })
    )
  }

  getDoctors(societiesIds: string[]){
    const body: any = societiesIds.length ? { userFilter: { societyIdList: societiesIds } } : {};

    return this.http.post<UserDetailResponse>(`${environment.URL_API}searchUsers`, body).pipe(
      map((response: UserDetailResponse) => {
        const users = response.users.filter((user: UserDetail) => user.externalId);
        const doctors = this.transformUserDetailToDoctor(users);
        return doctors;
      })
    );
    
  }


  getCenters(externalsIds?: string[]){
    //Si no tiene sociedad mandar un objeto vacio
    const body = externalsIds ? {externalUserIds: externalsIds} : {}
    return this.http.post<CentersResponse>(`${environment.URL_API}centers`, body).pipe(
      map( (response: CentersResponse ) => {
        return response.centers;
      })
    )
  }

  getBillingStatus(){
    return this.http.get<BillingStatusesResponse>(`${environment.URL_API}billingStatuses`).pipe(
      map( (response: BillingStatusesResponse ) => {
        return response.billingStatuses;
      })
    )
  }

  getCollectionStatus(){
    return this.http.get<CollectionStatusesResponse>(`${environment.URL_API}collectionStatuses`).pipe(
      map( (response: CollectionStatusesResponse ) => {
        return response.collectionStatuses;
      })
    )
  }

  getLiquidationsStatus(){
    return this.http.get<LiquidationStatusesResponse>(`${environment.URL_API}liquidationStatuses`).pipe(
      map( (response: LiquidationStatusesResponse ) => {
        return response.liquidationStatuses;
      })
    )
  }

  getFinanciers(externalsIds?: string[]){
    const body = externalsIds ? {externalUserIds: externalsIds} : {}
    return this.http.post<FinanciersResponse>(`${environment.URL_API}financiers`,body).pipe(
      map( (response: FinanciersResponse) => {
        return response.financiers;
      })
    )
  }

  getInsurances(externalsIds?: string[]){
    const body = externalsIds ? {externalUserIds: externalsIds} : {}
    return this.http.post<InsurancesResponse>(`${environment.URL_API}insurances`, body).pipe(
      map( (response: InsurancesResponse) => {
        return response.insurances.filter( a => a.description ).sort((a,b) => a.description > b.description ? 1 : -1 );
      })
    )
  }

  getAreas(externalsIds?: string[]){
    const body = externalsIds ? {externalUserIds: externalsIds} : {}
    return this.http.post<AreasResponse>(`${environment.URL_API}areas`, body).pipe(
      map( (response: AreasResponse) => {
        return response.areas;
      })
    )
  }

  getSpecialties(externalsIds?: string[]){
    const body = externalsIds ? {externalUserIds: externalsIds} : {}
    return this.http.post<SpecialtiesResponse>(`${environment.URL_API}specialties`, body).pipe(
      map( (response: SpecialtiesResponse) => {
        return response.specialties;
      })
    )
  }

  getGroups(externalsIds?: string[]){
    const body = externalsIds ? {externalUserIds: externalsIds} : {}
    return this.http.post<GroupsResponse>(`${environment.URL_API}groups`, body).pipe(
      map( (response: GroupsResponse) => {
        return response.groups;
      })
    )
  }

  getManagerSocietiesIds() {
    return this.managerSocietiesIds;
  }

  exportRemunerations(request: CriteriaRequestRemuneration, limit: number, offset: number, sort: any){
    let params = new HttpParams();
    params = params.append('additionalInfo.auditInfo', false);
    params = params.append('additionalInfo.resultList', true);
    params = params.append('additionalInfo.summaryInfo', false);
    params = params.append('paginate.limit', limit);
    params = params.append('paginate.offset', offset);
    if (sort.active && sort.direction) {
      const column = ORDERABLE_COLUMNS.find(c => c.id === sort.active);
      if (column) {
        params = params.append('orderBy.value', column.value);
        params = params.append('orderBy.order', sort.direction === 'asc' ? 'ASC' : 'DESC');
      }
    }
    const requestCriteria = this.mapperRemunerations(request, params);
    return this.http.get(`${environment.URL_API}remunerations/export`, { params: requestCriteria, responseType: 'blob', observe: 'response' } );
  }

  getRemunerations(request: CriteriaRequestRemuneration, limit: number, offset: number, sort: any) {
    let params = new HttpParams();
    params = params.append('additionalInfo.auditInfo', false);
    params = params.append('additionalInfo.resultList', true);
    params = params.append('additionalInfo.summaryInfo', true);

    params = params.append('additionalInfo.distinctInfo', true);
    params = params.append('additionalInfo.totalInfo', true);

    params = params.append('paginate.limit', limit);
    params = params.append('paginate.offset', offset);
    if (sort.active && sort.direction) {
      const column = ORDERABLE_COLUMNS.find(c => c.id === sort.active);
      if (column) {
        gtag('event', 'honorary_order-by-value-'+column.value.toLowerCase()+'-'+sort.direction, { content_type: 'filter' })
        gtag('event', 'honorary_order-'+sort.direction, { content_type: 'filter' })
        params = params.append('orderBy.value', column.value);
        params = params.append('orderBy.order', sort.direction === 'asc' ? 'ASC' : 'DESC');
      }
    }
    const requestCriteria = this.mapperRemunerations(request, params);
    return this.http.get(`${environment.URL_API}remunerations/search`, { params: requestCriteria });
  }

  mapperRemunerations(request: CriteriaRequestRemuneration, params: HttpParams): HttpParams{
    if( request?.companiesIds ){
      gtag('event', 'honorary_filter_companiesIds', { content_type: 'filter' })
      request.companiesIds.forEach( company =>{
        params = params.append( 'criteria.companiesIds', encodeURIComponent(company) );
      } );
    }

    if( request?.societiesIds && request?.societiesIds[0] !== undefined){
      gtag('event', 'honorary_filter_societyIds', { content_type: 'filter' })
      request.societiesIds.forEach( society => {
        params = params.append( 'criteria.societyIds', encodeURIComponent(society) );
      });
    }

    if( request?.externalIds && request?.externalIds[0] !== undefined){
      gtag('event', 'honorary_filter_doctorsIds', { content_type: 'filter' })
      request.externalIds.forEach( doctor => {
        params = params.append( 'criteria.externalUserIds', encodeURIComponent(doctor) );
      });
    }

    if( request?.centersIds ){
      gtag('event', 'honorary_filter_centersIds', { content_type: 'filter' })
      request.centersIds.forEach( center => {
        params = params.append( 'criteria.centersIds', encodeURIComponent(center) );
      });
    }

    if( request?.patientName ){
      gtag('event', 'honorary_filter_patientName', { content_type: 'filter' })
      params = params.append( 'criteria.patientName', encodeURIComponent(request.patientName) );
    }

    if( request?.billingStatusIds ){
      gtag('event', 'honorary_filter_billingStatusIds', { content_type: 'filter' })
      request.billingStatusIds.forEach( billingStatusId => {
        params =  params.append( 'criteria.billingStatusIds', encodeURIComponent(billingStatusId) )
      });
    }

    if( request?.collectionStatusIds ){
      gtag('event', 'honorary_filter_collectionStatusIds', { content_type: 'filter' })
      request.collectionStatusIds.forEach( collectionStatusId => {
       params =  params.append( 'criteria.collectionStatusIds', encodeURIComponent(collectionStatusId) )
      } );
    }

    if( request?.liquidationStatusIds ){
      gtag('event', 'honorary_filter_liquidationStatusIds', { content_type: 'filter' })
      request.liquidationStatusIds.forEach( liquidationStatusId => {
        params = params.append( 'criteria.liquidationStatusIds', encodeURIComponent(liquidationStatusId)  )
      } );
    }

    if( request?.attendanceFromDate ){
      gtag('event', 'honorary_filter_attendance_date', { content_type: 'filter' })
      params = params.append( 'criteria.attendanceFromDate' ,  request.attendanceFromDate);
    }

    if( request?.attendanceToDate ){
      params = params.append( 'criteria.attendanceToDate', request.attendanceToDate);
    }

    if( request?.billingFromDate ){
      gtag('event', 'honorary_filter_billing_date', { content_type: 'filter' })
      params = params.append( 'criteria.billingFromDate', request.billingFromDate);
    }

    if( request?.billingToDate ){
      params = params.append( 'criteria.billingToDate', request.billingToDate);
    }

    if( request?.attendanceNumber ){
      gtag('event', 'honorary_filter_attendanceNumber', { content_type: 'filter' })
      params = params.append( 'criteria.attendanceNumber', encodeURIComponent(request.attendanceNumber));
    }

    if( request?.financierIds ){
      gtag('event', 'honorary_filter_financierIds', { content_type: 'filter' })
      request.financierIds.forEach( financierId => {
        params = params.append( 'criteria.financierIds', encodeURIComponent(financierId));
      } );
    }

    if (request?.insuranceIds) {
      gtag('event', 'honorary_filter_insuranceIds', { content_type: 'filter' })
      request.insuranceIds.forEach(insuranceId => {
        params = params.append('criteria.insuranceIds', encodeURIComponent(insuranceId));
      });
    }

    if (request?.areaIds) {
      gtag('event', 'honorary_filter_areaIds', { content_type: 'filter' })
      request.areaIds.forEach(areaId => {
        params = params.append('criteria.areaIds', encodeURIComponent(areaId));
      });
    }
    if (request?.specialtyIds) {
      gtag('event', 'honorary_filter_specialtyIds', { content_type: 'filter' })
      request.specialtyIds.forEach(specialtyId => {
        params = params.append('criteria.specialtyIds', encodeURIComponent(specialtyId));
      }
      );
    }

    if( request?.attendance ){
      gtag('event', 'honorary_filter_attendance', { content_type: 'filter' })
      params = params.append('criteria.attendance', encodeURIComponent(request.attendance ));
    }

    if( request?.billingNumber ){
      gtag('event', 'honorary_filter_billingNumber', { content_type: 'filter' })
      params = params.append('criteria.billingNumber', request.billingNumber);
    }

    if( request?.collectionFromDate ){
      params = params.append('criteria.collectionFromDate', request.collectionFromDate );
    }

    if( request?.collectionToDate ){
      gtag('event', 'honorary_filter_collection_date', { content_type: 'filter'});
      params = params.append('criteria.collectionToDate', request.collectionToDate );
    }

    if( request?.liquidationNumber ){
      gtag('event', 'honorary_filter_liquidationNumber', { content_type: 'filter'});
      params = params.append('criteria.liquidationNumber', request.liquidationNumber );
    }

    if( request?.liquidationFromDate ){
      gtag('event', 'honorary_filter_liquidation_date', { content_type: 'filter'});
      params = params.append('criteria.liquidationFromDate', request.liquidationFromDate );
    }

    if( request?.liquidationToDate ){
      params = params.append('criteria.liquidationToDate', request.liquidationToDate );
    }

    if( request?.groupsIds ){
      gtag('event', 'honorary_filter_group', { content_type: 'filter'});
      request.groupsIds.forEach(group => {
        params = params.append('criteria.groupsIds', encodeURIComponent(group));
      });
    }

    if( request?.medicalHistoryNumber ){
      gtag('event', 'honorary_filter_medicalHistoryNumber', { content_type: 'filter' })
      params = params.append('criteria.medicalHistoryNumber', request.medicalHistoryNumber);
    }
    return params;
  }

  mapperRemunerationsMobile(request: any, params: HttpParams): HttpParams{

    if( request?.societiesIds && request?.societiesIds[0] !== undefined){
      gtag('event', 'honorary_filter_societyIds', { content_type: 'filter' })
      request.societiesIds.forEach( (society: string) => {
        params = params.append( 'criteria.societyIds', encodeURIComponent(society) );
      });
    }

    if( request?.externalIds && request?.externalIds[0] !== undefined){
      gtag('event', 'honorary_filter_doctorsIds', { content_type: 'filter' })
      request.externalIds.forEach( (doctor: string) => {
        params = params.append( 'criteria.externalUserIds', encodeURIComponent(doctor) );
      });
    }

    if( request?.centersIds ){
      gtag('event', 'honorary_filter_centersIds', { content_type: 'filter' })
      request.centersIds.forEach( (center: string) => {
        params = params.append( 'criteria.centersIds', encodeURIComponent(center) );
      });
    }

    if( request?.patientName ){
      gtag('event', 'honorary_filter_patientName', { content_type: 'filter' })
      params = params.append( 'criteria.patientName', encodeURIComponent(request.patientName) );
    }

    if( request?.collectionStatusIds ){
      gtag('event', 'honorary_filter_collectionStatusIds', { content_type: 'filter' })
      request.collectionStatusIds.forEach( (collectionStatusId: string) => {
       params =  params.append( 'criteria.collectionStatusIds', encodeURIComponent(collectionStatusId) )
      } );
    }

    if( request?.liquidationStatusIds ){
      gtag('event', 'honorary_filter_liquidationStatusIds', { content_type: 'filter' })
      request.liquidationStatusIds.forEach( (liquidationStatusId: string) => {
        params = params.append( 'criteria.liquidationStatusIds', encodeURIComponent(liquidationStatusId)  )
      } );
    }

    if( request?.billingStatusIds ){
      gtag('event', 'honorary_filter_billingStatusIds', { content_type: 'filter' })
      request.billingStatusIds.forEach( (billingStatusId: string) => {
        params = params.append( 'criteria.billingStatusIds', encodeURIComponent(billingStatusId)  )
      } );
    }

    if( request?.attendanceFromDate ){
      gtag('event', 'honorary_filter_attendance_date', { content_type: 'filter' })
      params = params.append( 'criteria.attendanceFromDate' ,  request.attendanceFromDate);
    }

    if( request?.attendanceToDate ){
      params = params.append( 'criteria.attendanceToDate', request.attendanceToDate);
    }

    if( request?.billingFromDate ){
      gtag('event', 'honorary_filter_billing_date', { content_type: 'filter' })
      params = params.append( 'criteria.billingFromDate' ,  request.billingFromDate);
    }

    if( request?.billingToDate){
      params = params.append( 'criteria.billingToDate', request.billingToDate);
    }

    if (request?.insuranceIds) {
      gtag('event', 'honorary_filter_insuranceIds', { content_type: 'filter' })
      request.insuranceIds.forEach((insuranceId: string) => {
        params = params.append('criteria.insuranceIds', encodeURIComponent(insuranceId));
      });
    }

    return params;
  }

  getStatsRemuneration( fromDate: string, toDate: string, liquidationStatus: string ){
    const requestRemuneration = `additionalInfo.auditInfo=false&additionalInfo.resultList=false&additionalInfo.summaryInfo=true&criteria.attendanceFromDate=${fromDate}&criteria.attendanceToDate=${toDate}&criteria.liquidationStatusIds=${liquidationStatus}`;
    return this.http.get<RemunerationStatsResponse>(`${environment.URL_API}remunerations/search?${requestRemuneration}`).pipe(
      map( (response: RemunerationStatsResponse) => {
        return response.summaryInfo.totalAttendanceAmount;
      })
    );
  }

  getMockRemuneration( request: CriteriaRequestRemuneration, offset: number = 0, sort?: any ){
    let params = new HttpParams();
    params = params.append( 'additionalInfo.auditInfo', false );
    params = params.append( 'additionalInfo.resultList', true );
    params = params.append( 'additionalInfo.summaryInfo', false );

    params = params.append('additionalInfo.distinctInfo', true);
    params = params.append('additionalInfo.totalInfo', true);

    params = params.append('paginate.limit', 15);
    if (sort?.active && sort?.direction) {
      const column = ORDERABLE_COLUMNS.find(c => c.id === sort.active);
      if (column) {
        params = params.append('orderBy.value', column.value);
        params = params.append('orderBy.order', sort.direction === 'asc' ? 'ASC' : 'DESC');
      }
    }
    const requestCriteria = this.mapperRemunerationsMobile( request, params);

    return this.http.get(`${environment.URL_API}remunerations/search?paginate.offset=${offset}`, {params: requestCriteria});
  }

  transformUserDetailToDoctor(users: UserDetail[]): Doctor[] {
    return users.map(user => ({
      externalId: user.externalId || '',
      firstName: user.firstName || '',
      id: user.id,
      langId: user.langId || '',
      lastName: user.lastName || '',
      pronounType: user.pronounType || '',
      specialityId: user.specialityId || 0
    }));
  }
  
  transformValuesToCriteriaRequest(values: any): CriteriaRequestRemuneration {
    let criteriaRQ: CriteriaRequestRemuneration = {};

    return criteriaRQ;
  }
}
